import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useId, useRef, useState } from 'react';
const CLEARANCE = 16;
/**
 * V105-4-6 A-H1: <html data-toast-on> and --toast-h are reference-counted across every mounted
 * toast (one closing never clears the marker while another is visible), and only the most
 * recently mounted toast answers Esc (one Esc dismisses one toast). Hosts should still render
 * a single toast; this keeps a stray second one from breaking the page.
 */
const stack = [];
export function Toast({ message, details, onUndo, action, onDismiss, returnFocus, version = 0, announce = true }) {
    const ref = useRef(null);
    const token = useRef(Symbol('toast'));
    const [open, setOpen] = useState(false);
    const [live, setLive] = useState('');
    const moreId = useId();
    if (!message?.trim())
        throw new Error('Toast: `message` is required');
    // Publish the height and mark <html> while visible.
    useEffect(() => {
        const el = ref.current;
        const root = document.documentElement;
        const publish = () => { if (el)
            root.style.setProperty('--toast-h', `${Math.ceil(el.getBoundingClientRect().height)}px`); };
        publish();
        stack.push(token.current);
        root.setAttribute('data-toast-on', '');
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
        if (el && ro)
            ro.observe(el);
        return () => {
            ro?.disconnect();
            const i = stack.lastIndexOf(token.current);
            if (i >= 0)
                stack.splice(i, 1);
            if (!stack.length) {
                root.removeAttribute('data-toast-on');
                root.style.removeProperty('--toast-h');
            }
        };
    }, []);
    // Announce the full message after a tick (a region that changes in the same frame it mounts is not read).
    useEffect(() => {
        setLive('');
        setOpen(false);
        const t = window.setTimeout(() => setLive(details ? `${message}. ${details}` : message), 60);
        return () => window.clearTimeout(t);
    }, [message, details, version]);
    // Focus never sits under the toast: scroll the focused element clear of its top edge.
    useEffect(() => {
        const onFocus = (e) => {
            const el = ref.current;
            const target = e.target;
            if (!el || !target || el.contains(target) || typeof target.getBoundingClientRect !== 'function')
                return;
            const top = el.getBoundingClientRect().top;
            const r = target.getBoundingClientRect();
            if (r.bottom > top - CLEARANCE)
                window.scrollBy({ top: r.bottom - (top - CLEARANCE), behavior: 'auto' });
        };
        document.addEventListener('focusin', onFocus);
        // The element focused before the toast mounted is checked once too.
        if (document.activeElement && document.activeElement !== document.body)
            onFocus({ target: document.activeElement });
        return () => document.removeEventListener('focusin', onFocus);
    }, [version]);
    // Esc dismisses without moving focus (unless focus was inside the toast).
    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== 'Escape' || e.defaultPrevented)
                return;
            if (stack[stack.length - 1] !== token.current)
                return; // only the top toast answers Esc
            // An open disclosure menu, an expanded control or a region marked [data-esc-owner] (an inline picker or form) owns this Esc: it closes first, the toast stays.
            const active = document.activeElement;
            if (active && !ref.current?.contains(active) && (active.closest?.('[data-part="menu"], [data-esc-owner]') || active.getAttribute?.('aria-expanded') === 'true'))
                return;
            const inside = !!ref.current?.contains(document.activeElement);
            onDismiss();
            if (inside)
                returnFocus?.focus();
        };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [onDismiss, returnFocus]);
    const close = (fn) => {
        const inside = !!ref.current?.contains(document.activeElement);
        fn();
        if (inside)
            returnFocus?.focus();
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { ref: ref, "data-toast": "", children: [_jsx("span", { "data-part": "text", children: message }), action ? _jsx("button", { type: "button", "data-part": "action", onClick: () => close(action.onClick), children: action.label }) : null, onUndo ? _jsx("button", { type: "button", "data-part": "undo", onClick: () => close(onUndo), children: "Undo" }) : null, details ? (_jsx("button", { type: "button", "data-part": "details", "aria-expanded": open, "aria-controls": moreId, onClick: () => setOpen((o) => !o), children: "Details" })) : null, _jsx("button", { type: "button", "data-part": "dismiss", onClick: () => close(onDismiss), children: "Dismiss" }), details ? _jsx("span", { id: moreId, "data-part": "more", hidden: !open, children: details }) : null] }), announce ? _jsx("p", { role: "status", "aria-live": "polite", "aria-atomic": "true", className: "sr-only", "data-part": "toast-live", children: live }) : null] }));
}
/**
 * The host's always-mounted announcement region for its toasts (render it once, unconditionally, and
 * pass `announce={false}` to the Toast). The text is written after a tick, so it is read even when the
 * toast and its message arrive in the same update; `version` re-announces the same text.
 */
export function ToastLiveRegion({ message, details, version = 0 }) {
    const [live, setLive] = useState('');
    useEffect(() => {
        setLive('');
        if (!message?.trim())
            return;
        const t = window.setTimeout(() => setLive(details ? `${message}. ${details}` : message), 60);
        return () => window.clearTimeout(t);
    }, [message, details, version]);
    return _jsx("p", { role: "status", "aria-live": "polite", "aria-atomic": "true", className: "sr-only", "data-part": "toast-live", children: live });
}
//# sourceMappingURL=Toast.js.map